/**
 * Décorateurs pour contrôler la sérialisation des modèles vers l'API.
 *
 * Usage :
 *   @OmitField()      → le champ est supprimé du payload (calculé côté serveur, read-only, lourd...)
 *   @FkField('code')  → le champ est réduit à { code: value.code } (clé étrangère)
 *   @NestedField()    → le champ est sérialisé récursivement en entier (objet imbriqué modifiable)
 *                       Équivalent au comportement par défaut — décorateur purement sémantique.
 *
 * Par défaut (aucun décorateur) → le champ est envoyé tel quel (comportement "nested").
 *
 * La clé FK peut toujours être surchargée : @FkField('alpha2') pour Country, par exemple.
 */

const OMIT_FIELDS = new Map<object, Set<string>>();
const FK_FIELDS = new Map<object, Map<string, string>>();

export function OmitField(): MethodDecorator {
    return (target: Object, propertyKey: string | symbol): void => {
        const key = String(propertyKey);
        if (!OMIT_FIELDS.has(target)) OMIT_FIELDS.set(target, new Set());
        OMIT_FIELDS.get(target)!.add(key);
    };
}

export function FkField(fkKey: string = 'id'): MethodDecorator {
    return (target: Object, propertyKey: string | symbol): void => {
        const key = String(propertyKey);
        if (!FK_FIELDS.has(target)) FK_FIELDS.set(target, new Map());
        FK_FIELDS.get(target)!.set(key, fkKey);
    };
}

/**
 * Marque un champ comme "nested" : l'objet lié est sérialisé récursivement et envoyé en entier.
 * Décorateur purement sémantique — toApi() recurse déjà sur tous les objets.
 * Utiliser pour documenter l'intention explicitement (vs @FkField qui réduit à une clé).
 */
export function NestedField(): MethodDecorator {
    return (_target: Object, _propertyKey: string | symbol): void => {
        // Intentionnellement vide : comportement déjà assuré par toApi() récursif.
    };
}

/**
 * Applique les métadonnées de décorateurs sur un objet plain déjà sérialisé par toApi().
 * Remonte toute la chaîne de prototypes pour collecter les métadonnées des classes parentes.
 *
 * @param data        L'objet plain produit par toApi() — modifié en place
 * @param modelClass  La classe du modèle source (ex : SalesInvoice)
 */
export function serializeForApi(data: any, modelClass: new (...args: any[]) => any): any {
    const omitFields = new Set<string>();
    const fkFields = new Map<string, string>();

    let proto = modelClass.prototype;
    while (proto && proto !== Object.prototype) {
        OMIT_FIELDS.get(proto)?.forEach(field => omitFields.add(field));
        FK_FIELDS.get(proto)?.forEach((fkKey, field) => {
            if (!fkFields.has(field)) fkFields.set(field, fkKey);
        });
        proto = Object.getPrototypeOf(proto);
    }

    for (const field of omitFields) {
        delete data[field];
    }

    for (const [field, fkKey] of fkFields) {
        if (data[field] == null) continue;
        if (Array.isArray(data[field])) {
            data[field] = data[field].map((item: any) => ({ [fkKey]: item[fkKey] }));
        } else {
            data[field] = { [fkKey]: data[field][fkKey] };
        }
    }

    return data;
}
